import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileDown, Lock, Download, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const DownloadPage = () => {
  const { shareToken } = useParams();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessCode, setAccessCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedUrl, setVerifiedUrl] = useState(null);

  const [isDownloading, setIsDownloading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchAsset();
  }, [shareToken]);

  const fetchAsset = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/download/${shareToken}`);
      if (response.ok) {
        const data = await response.json();
        setAsset(data);
        // If no access code required, set verified immediately
        if (!data.requires_code) {
          setIsVerified(true);
          setVerifiedUrl(data.file_url);
        }
      } else {
        const err = await response.json();
        setError(err.detail || 'Download link not found');
      }
    } catch (err) {
      setError('Failed to load download page');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setIsVerifying(true);

    try {
      const formData = new FormData();
      formData.append('access_code', accessCode);

      const response = await fetch(`${backendUrl}/api/download/${shareToken}/verify`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setIsVerified(true);
        setVerifiedUrl(data.file_url);
      } else {
        const err = await response.json();
        setError(err.detail || 'Invalid access code');
      }
    } catch (err) {
      setError('Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // GUARANTEED FILE DOWNLOAD - Forces browser download manager to save file
  // ═══════════════════════════════════════════════════════════════════════════
  const handleDownload = async () => {
    const fileUrl = verifiedUrl || asset?.file_url;
    if (!fileUrl) {
      setError('No file available for download');
      return;
    }
    
    setIsDownloading(true);
    setError(null);
    
    try {
      // Construct full URL for the file
      const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${backendUrl}${fileUrl}`;
      
      // Fetch the file as a blob - this forces actual download
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': '*/*'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      
      const blob = await response.blob();
      
      // Determine filename from URL or asset title
      const urlParts = fileUrl.split('/');
      const originalFileName = urlParts[urlParts.length - 1] || 'download';
      const fileName = asset?.title ? 
        `${asset.title}${originalFileName.includes('.') ? originalFileName.substring(originalFileName.lastIndexOf('.')) : ''}` : 
        originalFileName;
      
      // Create a temporary anchor element and force download
      const blobUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.style.display = 'none';
      downloadLink.href = blobUrl;
      downloadLink.download = fileName;
      
      // Force the download
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
    } catch (err) {
      console.error('Download error:', err);
      setError('Download failed. Please try again or contact support.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <Loader2 size={48} className="text-gold animate-spin" />
      </div>
    );
  }

  if (error && !asset) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle size={64} className="text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-cream mb-2">Download Not Available</h1>
          <p className="text-cream-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileDown size={40} className="text-gold" />
          </div>
          <h1 className="text-3xl font-script text-gold mb-2">
            {asset?.challenge_name || 'Digital Download'}
          </h1>
          <p className="text-cream-300">by Mona Bizzari</p>
        </div>

        {/* Content Card */}
        <div className="bg-charcoal-100 border border-white/10 rounded-2xl p-6 md:p-8">
          <h2 className="text-xl font-semibold text-cream mb-2">{asset?.title}</h2>
          {asset?.description && (
            <p className="text-cream-400 mb-6">{asset.description}</p>
          )}

          {/* Access Code Form (if not verified) */}
          {!isVerified && asset?.requires_code !== false && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                  <Lock size={14} className="inline mr-1" />
                  Access Code
                </label>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 bg-charcoal border border-white/10 rounded-lg text-cream text-center text-2xl font-mono tracking-widest focus:border-gold transition-colors"
                  placeholder="XXXXXXXX"
                  maxLength={8}
                  required
                />
              </div>
              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}
              <button
                type="submit"
                disabled={isVerifying || accessCode.length < 4}
                className="w-full bg-gold text-charcoal py-3 rounded-lg font-medium hover:bg-gold-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isVerifying ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    <span>Unlock Download</span>
                  </>
                )}
              </button>
            </form>
          )}

          {/* Download Button (if verified) */}
          {isVerified && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-emerald-400 mb-4">
                <CheckCircle size={20} />
                <span>Access Verified</span>
              </div>
              
              {/* Error message with retry option */}
              {error && (
                <div className="p-3 bg-red-900/30 border border-red-500/50 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={18} className="text-red-400" />
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                  <button 
                    onClick={() => setError(null)} 
                    className="text-red-400 hover:text-red-300 text-sm underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="w-full bg-gold text-charcoal py-4 rounded-lg font-medium hover:bg-gold-500 transition-colors flex items-center justify-center gap-2 text-lg disabled:opacity-70 disabled:cursor-wait"
              >
                {isDownloading ? (
                  <>
                    <Loader2 size={24} className="animate-spin" />
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <Download size={24} />
                    <span>{error ? 'Retry Download' : 'Download Now'}</span>
                  </>
                )}
              </button>
              <p className="text-cream-500 text-xs text-center">
                Downloads: {asset?.download_count || 0}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-cream-500 text-sm">
            Having trouble? Contact us at support@monabizzari.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
